import { ApplicationConfig, importProvidersFrom, isDevMode, NgZone } from '@angular/core';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import { HttpClientModule } from '@angular/common/http';
import { TimeagoClock, TimeagoDefaultClock, TimeagoDefaultFormatter, TimeagoFormatter } from 'ngx-timeago';

import { WeiToEthPipe } from './pipes/wei-to-eth.pipe';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { TraitCountPipe } from '@/pipes/trait-count.pipe';

import { routes } from './routes';
import { CustomReuseStrategy } from '@/routes/route.strategy';

import { environment } from '../environments/environment';

import { DecimalPipe } from '@angular/common';
import { MinBidPipe } from '@/pipes/auction-house/min-bid.pipe';
import { WeiPipe } from '@/pipes/auction-house/wei.pipe';

import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { dataStateReducer } from '@/store/auction-house/data.reducers';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEffects } from '@ngrx/effects';
import { DataStateEffects } from '@/store/auction-house/data.effects';
import { StarterBidPipe } from '@/pipes/auction-house/starter-bid';
import { UsdPipe } from '@/pipes/usd.pipe';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { APOLLO_OPTIONS, APOLLO_NAMED_OPTIONS, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache } from '@apollo/client/core';
import { isPlatformServer } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';

export const appConfig: ApplicationConfig = {
  providers: [
    // Apollo providers for client
    importProvidersFrom(ApolloModule),
    HttpLink,
    {
      provide: APOLLO_OPTIONS,
      useFactory: (httpLink: HttpLink) => {
        const platformId = inject(PLATFORM_ID);
        const ssrMode = isPlatformServer(platformId);
        const cache = new InMemoryCache({
          addTypename: false
        });

        console.log('[Apollo] Creating default client with SSR mode:', ssrMode, 'platformId:', platformId);
        console.log('[Apollo] GraphURI:', environment.graphURI);

        return {
          link: httpLink.create({ 
            uri: environment.graphURI,
          }),
          cache,
          ssrMode,
          connectToDevTools: !ssrMode,
          defaultOptions: {
            watchQuery: { 
              fetchPolicy: ssrMode ? 'network-only' as const : 'cache-and-network' as const,
              errorPolicy: 'all',
              notifyOnNetworkStatusChange: true
            },
            query: { 
              fetchPolicy: 'network-only' as const,
              errorPolicy: 'all'
            },
            mutate: {
              errorPolicy: 'all'
            }
          },
        };
      },
      deps: [HttpLink],
    },
    {
      provide: APOLLO_NAMED_OPTIONS,
      useFactory: (httpLink: HttpLink) => {
        const platformId = inject(PLATFORM_ID);
        const ssrMode = isPlatformServer(platformId);

        console.log('[Apollo] Creating named clients with SSR mode:', ssrMode, 'platformId:', platformId);
        console.log('[Apollo] Environment alternativeGraphURI:', environment.alternativeGraphURI);

        return {
          alternative: {
            link: httpLink.create({ 
              uri: environment.alternativeGraphURI || environment.graphURI,
            }),
            cache: new InMemoryCache({
              addTypename: false
            }),
            ssrMode,
            connectToDevTools: !ssrMode,
            defaultOptions: {
              watchQuery: { 
                fetchPolicy: ssrMode ? 'network-only' as const : 'cache-and-network' as const,
                errorPolicy: 'all'
              },
              query: { 
                fetchPolicy: 'network-only' as const,
                errorPolicy: 'all'
              },
              mutate: {
                errorPolicy: 'all'
              }
            },
          },
          alternativeListings: {
            link: httpLink.create({ 
              uri: environment.alternativeListingsGraphURI || environment.graphURI,
            }),
            cache: new InMemoryCache({
              addTypename: false
            }),
            ssrMode,
            connectToDevTools: !ssrMode,
            defaultOptions: {
              watchQuery: { 
                fetchPolicy: ssrMode ? 'network-only' as const : 'cache-and-network' as const,
                errorPolicy: 'all'
              },
              query: { 
                fetchPolicy: 'network-only' as const,
                errorPolicy: 'all'
              },
              mutate: {
                errorPolicy: 'all'
              }
            },
          },
          cig: {
            link: httpLink.create({ 
              uri: environment.cigGraphURI || environment.graphURI,
            }),
            cache: new InMemoryCache({
              addTypename: false
            }),
            ssrMode,
            connectToDevTools: !ssrMode,
            defaultOptions: {
              watchQuery: { 
                fetchPolicy: ssrMode ? 'network-only' as const : 'cache-and-network' as const,
                errorPolicy: 'all'
              },
              query: { 
                fetchPolicy: 'network-only' as const,
                errorPolicy: 'all'
              },
              mutate: {
                errorPolicy: 'all'
              }
            },
          },
          nftx: {
            link: httpLink.create({ 
              uri: environment.nftxGraphURI || environment.graphURI,
            }),
            cache: new InMemoryCache({
              addTypename: false
            }),
            ssrMode,
            connectToDevTools: !ssrMode,
            defaultOptions: {
              watchQuery: { 
                fetchPolicy: ssrMode ? 'network-only' as const : 'cache-and-network' as const,
                errorPolicy: 'all'
              },
              query: { 
                fetchPolicy: 'network-only' as const,
                errorPolicy: 'all'
              },
              mutate: {
                errorPolicy: 'all'
              }
            },
          },
          auctionHouse: {
            link: httpLink.create({ 
              uri: environment.auctionHouseUri,
            }),
            cache: new InMemoryCache({
              addTypename: false
            }),
            ssrMode,
            connectToDevTools: !ssrMode,
            defaultOptions: {
              watchQuery: { 
                fetchPolicy: ssrMode ? 'network-only' as const : 'cache-and-network' as const,
                errorPolicy: 'all'
              },
              query: { 
                fetchPolicy: 'network-only' as const,
                errorPolicy: 'all'
              },
              mutate: {
                errorPolicy: 'all'
              }
            },
          },
          gondi: {
            link: httpLink.create({ 
              uri: environment.gondiUrl,
            }),
            cache: new InMemoryCache({
              addTypename: false
            }),
            ssrMode,
            connectToDevTools: !ssrMode,
            defaultOptions: {
              watchQuery: { 
                fetchPolicy: ssrMode ? 'network-only' as const : 'cache-and-network' as const,
                errorPolicy: 'all'
              },
              query: { 
                fetchPolicy: 'network-only' as const,
                errorPolicy: 'all'
              },
              mutate: {
                errorPolicy: 'all'
              }
            },
          },

          states: {
            link: httpLink.create({ 
              uri: environment.statesGraphURI || environment.graphURI,
            }),
            cache: new InMemoryCache({
              addTypename: false
            }),
            ssrMode,
            connectToDevTools: !ssrMode,
            defaultOptions: {
              watchQuery: { 
                fetchPolicy: ssrMode ? 'network-only' : 'cache-and-network',
                errorPolicy: 'all'
              },
              query: { 
                fetchPolicy: 'network-only',
                errorPolicy: 'all'
              },
              mutate: {
                errorPolicy: 'all'
              }
            },
          },          
        };
      },
      deps: [HttpLink],
    },
    
    // Basic pipes
    { provide: WeiPipe, useClass: WeiPipe },
    { provide: MinBidPipe, useClass: MinBidPipe },
    { provide: DecimalPipe, useClass: DecimalPipe },
    { provide: UsdPipe, useClass: UsdPipe },
    { provide: WeiToEthPipe, useClass: WeiToEthPipe },
    { provide: TokenIdParsePipe, useClass: TokenIdParsePipe },
    { provide: TraitCountPipe, useClass: TraitCountPipe },
    { provide: StarterBidPipe, useClass: StarterBidPipe },

    // Timeago configuration
    { provide: TimeagoFormatter, useClass: TimeagoDefaultFormatter },
    { provide: TimeagoClock, useClass: TimeagoDefaultClock },

    // Route strategy
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },

    // HTTP client with Fetch API support
    provideHttpClient(withFetch()),
    importProvidersFrom(HttpClientModule),

    // Router
    provideRouter(routes),
    provideRouterStore(),

    // Store (simplified)
    provideStore({
      dataState: dataStateReducer,
      router: routerReducer
    }),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !isDevMode(),
      trace: false // Disabled for debugging
    }),
    provideEffects([
      DataStateEffects
    ]),

    // Service worker
    provideServiceWorker(`ngsw-worker.js?v=20052025`, {
        enabled: environment.production,
        registrationStrategy: 'registerImmediately',
    }),
  ]
}; 